import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ForceGraph2D from 'react-force-graph-2d';
import api from '../utils/axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Network,
  Search,
  RefreshCw,
  ZoomIn,
  ZoomOut,
  AlertTriangle,
  Users,
  Info,
  ArrowUpRight,
  ArrowDownLeft,
  TrendingUp,
  DollarSign,
  Calendar,
  MoreHorizontal,
  X,
  Filter,
  Eye,
  EyeOff,
  Target,
  Share2,
  Zap,
  Layers,
  Split,
  Moon,
  Circle,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  FileText,
  PlusCircle,
  Activity
} from 'lucide-react';

// Formatear montos en pesos argentinos
const formatMoney = (amount) => {
  if (!amount && amount !== 0) return '$0';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Formatear números grandes
const formatNumber = (num) => {
  if (!num && num !== 0) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

// Iconos para cada tipo de patrón
const PATTERN_ICONS = {
  STRUCTURING: Split,
  CONCENTRATOR: Target,
  DISPERSER: Share2,
  HIGH_VELOCITY: Zap,
  CIRCULAR: RefreshCw,
  SMURFING: Users,
  LAYERING: Layers,
  DORMANT_ACTIVATION: AlertCircle,
  ROUND_AMOUNTS: Circle,
  OFF_HOURS: Moon
};

// Colores para severidad de patrones
const SEVERITY_COLORS = {
  critical: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
  high: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
  medium: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' },
  low: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' }
};

const LinkAnalysisPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const graphRef = useRef();
  
  // Estados principales
  const [accountId, setAccountId] = useState(searchParams.get('cuenta') || '');
  const [depth, setDepth] = useState('2');
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [networks, setNetworks] = useState([]);
  const [showNetworks, setShowNetworks] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 800, height: 500 });
  const [topCounterparts, setTopCounterparts] = useState(null);
  const [loadingCounterparts, setLoadingCounterparts] = useState(false);
  
  // Estados para análisis de patrones
  const [nodePatterns, setNodePatterns] = useState(null);
  const [loadingPatterns, setLoadingPatterns] = useState(false);
  const [showPatternPanel, setShowPatternPanel] = useState(false);
  
  // Estados para filtros
  const [filters, setFilters] = useState({
    showOnlyAlerts: false,
    showOnlyHighRisk: false,
    minVolume: 0,
    minTransactions: 0,
    hideNoPatterns: false
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Estado para vista simplificada
  const [viewMode, setViewMode] = useState('full'); // full, alerts, patterns

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      const container = document.getElementById('graph-container');
      if (container) {
        setDimensions({
          width: container.clientWidth,
          height: Math.max(500, window.innerHeight - 380)
        });
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load initial account from URL
  useEffect(() => {
    const cuenta = searchParams.get('cuenta');
    if (cuenta) {
      setAccountId(cuenta);
      loadAccountLinks(cuenta);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ==================== FUNCIONES HELPER (antes del useMemo) ====================
  
  const getLinkColor = (monto, direction) => {
    const baseColors = direction === 'salida' 
      ? { high: '#dc2626', medium: '#f97316', low: '#94a3b8' }
      : { high: '#16a34a', medium: '#22c55e', low: '#94a3b8' };
    
    if (monto >= 1000000) return baseColors.high;
    if (monto >= 100000) return baseColors.medium;
    return baseColors.low;
  };

  const getNodeColor = (riskScore, isRoot, suspiciousPatterns = []) => {
    if (isRoot) return '#2563eb';
    if (suspiciousPatterns && suspiciousPatterns.length > 0) return '#dc2626';
    if (riskScore >= 70) return '#dc2626';
    if (riskScore >= 40) return '#f59e0b';
    return '#22c55e';
  };

  const getNodeSize = (node) => {
    const volumen = (node.total_recibido || 0) + (node.total_enviado || 0);
    if (node.is_root) return 15;
    if (volumen >= 10000000) return 12;
    if (volumen >= 1000000) return 10;
    if (volumen >= 100000) return 8;
    return 6;
  };

  // ==================== FIN FUNCIONES HELPER ====================

  // Filtrar datos del grafo basado en filtros activos
  const filteredGraphData = useMemo(() => {
    if (!rawData) return { nodes: [], links: [] };
    
    let filteredNodes = rawData.nodes;
    
    // Filtrar por alertas
    if (filters.showOnlyAlerts) {
      filteredNodes = filteredNodes.filter(n => n.alerts_count > 0 || n.is_root);
    }
    
    // Filtrar por riesgo alto
    if (filters.showOnlyHighRisk) {
      filteredNodes = filteredNodes.filter(n => 
        n.risk_score >= 50 || 
        n.is_root || 
        (n.suspicious_patterns && n.suspicious_patterns.length > 0)
      );
    }
    
    // Filtrar por volumen mínimo
    if (filters.minVolume > 0) {
      filteredNodes = filteredNodes.filter(n => 
        (n.total_recibido + n.total_enviado) >= filters.minVolume || n.is_root
      );
    }
    
    // Filtrar por cantidad mínima de transacciones
    if (filters.minTransactions > 0) {
      filteredNodes = filteredNodes.filter(n => 
        (n.cantidad_entradas + n.cantidad_salidas) >= filters.minTransactions || n.is_root
      );
    }
    
    // Ocultar nodos sin patrones
    if (filters.hideNoPatterns) {
      filteredNodes = filteredNodes.filter(n => 
        (n.suspicious_patterns && n.suspicious_patterns.length > 0) || n.is_root
      );
    }
    
    // Obtener IDs de nodos filtrados
    const nodeIds = new Set(filteredNodes.map(n => n.id));
    
    // Filtrar enlaces que conecten nodos visibles
    const filteredEdges = rawData.edges.filter(e => 
      nodeIds.has(e.source) && nodeIds.has(e.target)
    );
    
    // Transformar para el grafo
    const nodes = filteredNodes.map(node => ({
      id: node.id,
      label: node.label,
      risk_score: node.risk_score,
      alerts_count: node.alerts_count,
      is_root: node.is_root,
      is_waya: node.is_waya,
      color: getNodeColor(node.risk_score, node.is_root, node.suspicious_patterns),
      size: getNodeSize(node),
      total_recibido: node.total_recibido || 0,
      total_enviado: node.total_enviado || 0,
      cantidad_entradas: node.cantidad_entradas || 0,
      cantidad_salidas: node.cantidad_salidas || 0,
      origenes_distintos: node.origenes_distintos || 0,
      destinos_distintos: node.destinos_distintos || 0,
      primera_operacion: node.primera_operacion,
      ultima_operacion: node.ultima_operacion,
      suspicious_patterns: node.suspicious_patterns || [],
      has_more_connections: node.has_more_connections
    }));
    
    const links = filteredEdges.map(edge => ({
      source: edge.source,
      target: edge.target,
      direction: edge.direction,
      type: edge.type,
      cantidad_txs: edge.cantidad_txs || edge.weight || 1,
      monto_total: edge.monto_total || 0,
      monto_promedio: edge.monto_promedio || 0,
      primera_tx: edge.primera_tx,
      ultima_tx: edge.ultima_tx,
      frecuencia_dias: edge.frecuencia_dias || 0,
      color: getLinkColor(edge.monto_total, edge.direction),
      width: Math.max(1, Math.min(8, Math.log10(edge.monto_total || 1)))
    }));
    
    return { nodes, links };
  }, [rawData, filters]);

  // Actualizar graphData cuando cambien los filtros
  useEffect(() => {
    setGraphData(filteredGraphData);
  }, [filteredGraphData]);

  const loadAccountLinks = async (account = accountId) => {
    if (!account.trim()) {
      toast.error('Ingrese un número de cuenta');
      return;
    }
    
    setLoading(true);
    setSelectedNode(null);
    setSelectedEdge(null);
    setTopCounterparts(null);
    setNodePatterns(null);
    
    try {
      const res = await api.get(`/links/account/${account}`, {
        params: { depth: parseInt(depth) }
      });
      
      setRawData(res.data);
      setSearchParams({ cuenta: account });
      
      const summary = res.data.summary;
      toast.success(
        `${res.data.nodes.length} nodos, ${res.data.edges.length} conexiones cargadas`
      );
    } catch (error) {
      toast.error('Error al analizar vínculos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadTopCounterparts = async (cuentaId) => {
    setLoadingCounterparts(true);
    try {
      const res = await api.get(`/links/counterparts/${cuentaId}?limit=5`);
      setTopCounterparts(res.data);
    } catch (error) {
      console.error('Error loading counterparts:', error);
    } finally {
      setLoadingCounterparts(false);
    }
  };

  const loadNodePatterns = async (cuentaId) => {
    setLoadingPatterns(true);
    try {
      const res = await api.get(`/links/patterns/analyze/${cuentaId}`);
      setNodePatterns(res.data);
      setShowPatternPanel(true);
    } catch (error) {
      console.error('Error loading patterns:', error);
      toast.error('Error al analizar patrones');
    } finally {
      setLoadingPatterns(false);
    }
  };

  const loadNetworks = async () => {
    setLoading(true);
    try {
      const res = await api.get('/links/networks?min_connections=3');
      setNetworks(res.data.networks || []);
      setShowNetworks(true);
      
      if (res.data.networks?.length > 0) {
        toast.success(`${res.data.total_detected} redes sospechosas detectadas`);
      } else {
        toast.info('No se encontraron redes sospechosas');
      }
    } catch (error) {
      toast.error('Error al detectar redes');
    } finally {
      setLoading(false);
    }
  };

  const handleNodeClick = useCallback((node) => {
    setSelectedNode(node);
    setSelectedEdge(null);
    loadTopCounterparts(node.id);
    
    if (graphRef.current) {
      graphRef.current.centerAt(node.x, node.y, 1000);
      graphRef.current.zoom(2, 1000);
    }
  }, []);

  const handleLinkClick = useCallback((link) => {
    setSelectedEdge(link);
    setSelectedNode(null);
  }, []);

  const handleZoom = (direction) => {
    if (graphRef.current) {
      const currentZoom = graphRef.current.zoom();
      graphRef.current.zoom(direction === 'in' ? currentZoom * 1.5 : currentZoom / 1.5, 300);
    }
  };

  const analyzeNetwork = (network) => {
    setAccountId(network.root_account);
    loadAccountLinks(network.root_account);
    setShowNetworks(false);
  };

  const getPatternLabel = (pattern) => {
    const labels = {
      'concentrador': '🎯 Concentrador',
      'dispersor': '📤 Dispersor',
      'solo_recibe': '📥 Solo Recibe',
      'solo_envia': '📤 Solo Envía',
      'normal': '✓ Normal'
    };
    return labels[pattern] || pattern;
  };

  const getPatternDescription = (patterns) => {
    if (!patterns || patterns.length === 0) return null;
    
    const descriptions = {
      'concentrador': 'Recibe de muchas fuentes y envía a pocas',
      'dispersor': 'Recibe de pocas fuentes y envía a muchas',
      'solo_recibe': 'Esta cuenta solo recibe fondos',
      'solo_envia': 'Esta cuenta solo envía fondos'
    };
    
    return patterns.map(p => descriptions[p]).filter(Boolean);
  };

  const resetFilters = () => {
    setFilters({
      showOnlyAlerts: false,
      showOnlyHighRisk: false,
      minVolume: 0,
      minTransactions: 0,
      hideNoPatterns: false
    });
  };

  const hasActiveFilters = () => {
    return filters.showOnlyAlerts || 
           filters.showOnlyHighRisk || 
           filters.minVolume > 0 || 
           filters.minTransactions > 0 ||
           filters.hideNoPatterns;
  };

  const createCaseFromNode = (node) => {
    navigate(`/m/gestion-casos/cases/new?cuenta=${node.id}&nombre=${encodeURIComponent(node.label)}`);
  };

  const createAlertFromNode = (node) => {
    toast.info('Funcionalidad de crear alerta en desarrollo');
  };

  return (
    <div className="p-4 lg:p-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Network className="text-purple-600" />
            Análisis de Vínculos
          </h1>
          <p className="text-sm text-slate-600">
            Visualiza relaciones y detecta patrones sospechosos
          </p>
        </div>
        <div className="flex gap-2 mt-3 lg:mt-0">
          <Button
            variant={showFilters ? "default" : "outline"}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={hasActiveFilters() ? "border-purple-500" : ""}
          >
            <Filter size={14} className="mr-1" />
            Filtros
            {hasActiveFilters() && (
              <Badge className="ml-1 bg-purple-600 text-white text-xs px-1">!</Badge>
            )}
          </Button>
          <Button
            onClick={loadNetworks}
            variant="outline"
            size="sm"
            disabled={loading}
          >
            {loading ? (
              <RefreshCw size={14} className="mr-1 animate-spin" />
            ) : (
              <Users size={14} className="mr-1" />
            )}
            Detectar Redes
          </Button>
        </div>
      </div>

      {/* Search Bar y Filtros */}
      <Card className="p-3 mb-4 bg-white">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs text-slate-500">Cuenta a Analizar</Label>
            <Input
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              placeholder="Número de cuenta o CVU"
              style={{ backgroundColor: '#fff' }}
              onKeyPress={(e) => e.key === 'Enter' && loadAccountLinks()}
              className="h-9"
            />
          </div>
          <div className="w-28">
            <Label className="text-xs text-slate-500">Profundidad</Label>
            <Select value={depth} onValueChange={setDepth}>
              <SelectTrigger style={{ backgroundColor: '#fff' }} className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 nivel</SelectItem>
                <SelectItem value="2">2 niveles</SelectItem>
                <SelectItem value="3">3 niveles</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={() => loadAccountLinks()}
            disabled={loading}
            style={{ backgroundColor: '#7c3aed' }}
            className="h-9"
          >
            {loading ? (
              <RefreshCw size={14} className="mr-1 animate-spin" />
            ) : (
              <Search size={14} className="mr-1" />
            )}
            Analizar
          </Button>
        </div>

        {/* Panel de Filtros Expandible */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <Filter size={14} />
                Filtros Inteligentes
              </h4>
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                Limpiar
              </Button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={filters.showOnlyAlerts}
                  onCheckedChange={(v) => setFilters(f => ({ ...f, showOnlyAlerts: v }))}
                />
                <span className="text-xs text-slate-600">Solo con alertas</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={filters.showOnlyHighRisk}
                  onCheckedChange={(v) => setFilters(f => ({ ...f, showOnlyHighRisk: v }))}
                />
                <span className="text-xs text-slate-600">Solo alto riesgo</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={filters.hideNoPatterns}
                  onCheckedChange={(v) => setFilters(f => ({ ...f, hideNoPatterns: v }))}
                />
                <span className="text-xs text-slate-600">Solo sospechosos</span>
              </label>
              
              <div>
                <Label className="text-xs text-slate-500">Vol. mín. ($)</Label>
                <Input
                  type="number"
                  value={filters.minVolume}
                  onChange={(e) => setFilters(f => ({ ...f, minVolume: parseInt(e.target.value) || 0 }))}
                  placeholder="0"
                  className="h-8 text-sm"
                  style={{ backgroundColor: '#fff' }}
                />
              </div>
              
              <div>
                <Label className="text-xs text-slate-500">Txs mín.</Label>
                <Input
                  type="number"
                  value={filters.minTransactions}
                  onChange={(e) => setFilters(f => ({ ...f, minTransactions: parseInt(e.target.value) || 0 }))}
                  placeholder="0"
                  className="h-8 text-sm"
                  style={{ backgroundColor: '#fff' }}
                />
              </div>
            </div>
            
            {rawData && (
              <div className="mt-3 text-xs text-slate-500">
                Mostrando {graphData.nodes.length} de {rawData.nodes.length} nodos
                {hasActiveFilters() && " (filtros activos)"}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Networks Detected */}
      {showNetworks && networks.length > 0 && (
        <Card className="p-4 mb-4 bg-white border-purple-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Network className="text-purple-600" size={18} />
              {networks.length} Redes Detectadas
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setShowNetworks(false)}>
              <X size={14} />
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            {networks.slice(0, 8).map((net, idx) => (
              <div
                key={idx}
                className="p-3 bg-slate-50 rounded-lg hover:bg-purple-50 cursor-pointer border border-slate-200 hover:border-purple-300 transition-all"
                onClick={() => analyzeNetwork(net)}
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="font-mono text-xs text-slate-700 truncate max-w-[120px]">
                    {net.root_account?.substring(0, 16)}...
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    net.average_risk >= 70 ? 'bg-red-100 text-red-700' :
                    net.average_risk >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                  }`}>
                    {net.average_risk}%
                  </span>
                </div>
                
                {net.pattern && net.pattern !== 'normal' && (
                  <div className="text-xs text-red-600 font-medium mb-1">
                    {getPatternLabel(net.pattern)}
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <div className="flex items-center gap-1 text-green-600">
                    <ArrowDownLeft size={10} />
                    {formatMoney(net.volumen_entrada || 0)}
                  </div>
                  <div className="flex items-center gap-1 text-red-600">
                    <ArrowUpRight size={10} />
                    {formatMoney(net.volumen_salida || 0)}
                  </div>
                </div>
                
                <div className="text-xs text-slate-400 mt-1">
                  {net.total_operaciones?.toLocaleString() || 0} ops • {net.total_nodes} nodos
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Graph */}
        <Card className="lg:col-span-3 bg-white overflow-hidden" id="graph-container">
          <div className="p-2 border-b flex justify-between items-center">
            <span className="text-sm font-medium text-slate-700">
              Grafo de Conexiones
              {graphData.nodes.length > 0 && (
                <span className="text-slate-400 ml-2 text-xs">
                  ({graphData.nodes.length} nodos, {graphData.links.length} enlaces)
                </span>
              )}
            </span>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => handleZoom('in')} className="h-7 w-7 p-0">
                <ZoomIn size={14} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleZoom('out')} className="h-7 w-7 p-0">
                <ZoomOut size={14} />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => graphRef.current?.zoomToFit(400)}
                className="h-7 w-7 p-0"
              >
                <RefreshCw size={14} />
              </Button>
            </div>
          </div>
          
          <div style={{ height: dimensions.height, backgroundColor: '#f8fafc' }}>
            {graphData.nodes.length === 0 ? (
              <div className="flex items-center justify-center h-full text-slate-400">
                <div className="text-center">
                  <Network size={48} className="mx-auto mb-2 opacity-50" />
                  <p>Ingrese una cuenta para visualizar sus vínculos</p>
                </div>
              </div>
            ) : (
              <ForceGraph2D
                ref={graphRef}
                graphData={graphData}
                width={dimensions.width}
                height={dimensions.height}
                nodeLabel={(node) => {
                  return `${node.label}\nCuenta: ${node.id}\nRiesgo: ${node.risk_score}\nRecibido: ${formatMoney(node.total_recibido)}\nEnviado: ${formatMoney(node.total_enviado)}`;
                }}
                linkLabel={(link) => {
                  const dir = link.direction === 'salida' ? '→ Envío' : '← Recepción';
                  return `${dir}\nMonto: ${formatMoney(link.monto_total)}\nTransacciones: ${link.cantidad_txs}`;
                }}
                nodeColor={(node) => node.color}
                nodeRelSize={1}
                nodeVal={(node) => node.size}
                linkColor={(link) => link.color}
                linkWidth={(link) => link.width}
                linkDirectionalArrowLength={6}
                linkDirectionalArrowRelPos={0.9}
                linkCurvature={0.2}
                onNodeClick={handleNodeClick}
                onLinkClick={handleLinkClick}
                cooldownTicks={100}
                nodeCanvasObject={(node, ctx, globalScale) => {
                  const size = node.size;
                  
                  ctx.beginPath();
                  ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
                  ctx.fillStyle = node.color;
                  ctx.fill();
                  
                  if (node.is_root) {
                    ctx.strokeStyle = '#1e40af';
                    ctx.lineWidth = 3;
                    ctx.stroke();
                  } else if (node.alerts_count > 0) {
                    ctx.strokeStyle = '#dc2626';
                    ctx.lineWidth = 2;
                    ctx.setLineDash([3, 3]);
                    ctx.stroke();
                    ctx.setLineDash([]);
                  } else if (node.suspicious_patterns && node.suspicious_patterns.length > 0) {
                    ctx.strokeStyle = '#f59e0b';
                    ctx.lineWidth = 2;
                    ctx.stroke();
                  }
                  
                  if (node.is_waya && !node.is_root) {
                    ctx.fillStyle = '#3b82f6';
                    ctx.beginPath();
                    ctx.arc(node.x + size * 0.7, node.y - size * 0.7, 3, 0, 2 * Math.PI);
                    ctx.fill();
                  }
                  
                  if (globalScale > 1.2) {
                    ctx.font = `${Math.max(8, 10/globalScale)}px Inter, sans-serif`;
                    ctx.fillStyle = '#334155';
                    ctx.textAlign = 'center';
                    ctx.fillText(node.label.substring(0, 12), node.x, node.y + size + 10/globalScale);
                  }
                }}
              />
            )}
          </div>
        </Card>

        {/* Side Panel */}
        <div className="space-y-3">
          {/* Legend */}
          <Card className="p-3 bg-white">
            <h3 className="text-xs font-semibold text-slate-700 mb-2">Leyenda</h3>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-600 border-2 border-blue-900"></div>
                <span>Cuenta analizada</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-600"></div>
                <span>Alto riesgo / Sospechoso</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <span>Riesgo medio</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span>Bajo riesgo</span>
              </div>
              <div className="mt-2 pt-2 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-0.5 bg-red-500"></div>
                  <span>Envío</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-0.5 bg-green-500"></div>
                  <span>Recepción</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Selected Node Info */}
          {selectedNode && (
            <Card className="p-3 bg-white">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                  <Info size={12} />
                  Detalle del Nodo
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setSelectedNode(null)} className="h-6 w-6 p-0">
                  <X size={12} />
                </Button>
              </div>
              
              <Tabs defaultValue="info" className="w-full">
                <TabsList className="grid w-full grid-cols-2 h-7">
                  <TabsTrigger value="info" className="text-xs">Info</TabsTrigger>
                  <TabsTrigger value="patterns" className="text-xs">Patrones</TabsTrigger>
                </TabsList>
                
                <TabsContent value="info" className="mt-2 space-y-2">
                  <div>
                    <span className="text-slate-500 text-xs">Cuenta:</span>
                    <p className="font-mono text-slate-900 text-xs break-all">{selectedNode.id}</p>
                  </div>
                  
                  <div>
                    <span className="text-slate-500 text-xs">Nombre:</span>
                    <p className="text-slate-900 text-sm">{selectedNode.label}</p>
                  </div>
                  
                  {/* Patrones sospechosos inline */}
                  {selectedNode.suspicious_patterns && selectedNode.suspicious_patterns.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded p-2">
                      <div className="flex items-center gap-1 text-red-700 font-medium text-xs mb-1">
                        <AlertTriangle size={10} />
                        Sospechoso
                      </div>
                      {getPatternDescription(selectedNode.suspicious_patterns)?.map((desc, i) => (
                        <p key={desc} className="text-red-600 text-xs">{desc}</p>
                      ))}
                    </div>
                  )}
                  
                  {/* Flujo de fondos */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-green-50 rounded p-2">
                      <div className="flex items-center gap-1 text-green-700 text-xs">
                        <ArrowDownLeft size={10} />
                        Recibido
                      </div>
                      <p className="font-bold text-green-700 text-sm">{formatMoney(selectedNode.total_recibido)}</p>
                      <p className="text-xs text-green-600">{selectedNode.cantidad_entradas} txs</p>
                    </div>
                    <div className="bg-red-50 rounded p-2">
                      <div className="flex items-center gap-1 text-red-700 text-xs">
                        <ArrowUpRight size={10} />
                        Enviado
                      </div>
                      <p className="font-bold text-red-700 text-sm">{formatMoney(selectedNode.total_enviado)}</p>
                      <p className="text-xs text-red-600">{selectedNode.cantidad_salidas} txs</p>
                    </div>
                  </div>
                  
                  {/* Métricas */}
                  <div className="flex gap-3 text-xs border-t pt-2">
                    <div>
                      <span className="text-slate-500">Orígenes:</span>
                      <p className="font-bold">{selectedNode.origenes_distintos}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Destinos:</span>
                      <p className="font-bold">{selectedNode.destinos_distintos}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Alertas:</span>
                      <p className={`font-bold ${selectedNode.alerts_count > 0 ? 'text-red-600' : ''}`}>
                        {selectedNode.alerts_count}
                      </p>
                    </div>
                  </div>
                  
                  {/* Acciones */}
                  <div className="flex gap-2 pt-2 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 h-7 text-xs"
                      onClick={() => {
                        setAccountId(selectedNode.id);
                        loadAccountLinks(selectedNode.id);
                      }}
                    >
                      <Search size={10} className="mr-1" />
                      Analizar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 h-7 text-xs"
                      onClick={() => createCaseFromNode(selectedNode)}
                    >
                      <FileText size={10} className="mr-1" />
                      Crear Caso
                    </Button>
                  </div>
                </TabsContent>
                
                <TabsContent value="patterns" className="mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mb-2 text-xs h-7"
                    onClick={() => loadNodePatterns(selectedNode.id)}
                    disabled={loadingPatterns}
                  >
                    {loadingPatterns ? (
                      <RefreshCw size={10} className="mr-1 animate-spin" />
                    ) : (
                      <Activity size={10} className="mr-1" />
                    )}
                    Analizar Patrones
                  </Button>
                  
                  {nodePatterns && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500">Score de Riesgo:</span>
                        <Badge className={`${
                          nodePatterns.risk_score >= 70 ? 'bg-red-100 text-red-700' :
                          nodePatterns.risk_score >= 40 ? 'bg-amber-100 text-amber-700' : 
                          'bg-green-100 text-green-700'
                        }`}>
                          {nodePatterns.risk_score}%
                        </Badge>
                      </div>
                      
                      {nodePatterns.patterns && nodePatterns.patterns.length > 0 ? (
                        <div className="space-y-2">
                          {nodePatterns.patterns.map((pattern, idx) => {
                            const Icon = PATTERN_ICONS[pattern.type] || AlertTriangle;
                            const colors = SEVERITY_COLORS[pattern.severity] || SEVERITY_COLORS.low;
                            
                            return (
                              <div key={idx} className={`p-2 rounded ${colors.bg} border ${colors.border}`}>
                                <div className={`flex items-center gap-1 font-medium text-xs ${colors.text}`}>
                                  <Icon size={12} />
                                  {pattern.name}
                                </div>
                                <p className={`text-xs mt-1 ${colors.text} opacity-80`}>
                                  {pattern.details}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400 text-center py-3">
                          No se detectaron patrones sospechosos
                        </p>
                      )}
                      
                      {nodePatterns.risk_factors && nodePatterns.risk_factors.length > 0 && (
                        <div className="mt-2 pt-2 border-t">
                          <span className="text-xs font-medium text-slate-700">Factores de Riesgo:</span>
                          <ul className="mt-1 space-y-1">
                            {nodePatterns.risk_factors.map((factor, idx) => (
                              <li key={idx} className="text-xs text-slate-600 flex items-start gap-1">
                                <AlertTriangle size={10} className="text-amber-500 mt-0.5 flex-shrink-0" />
                                {factor}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </Card>
          )}

          {/* Top Counterparts */}
          {selectedNode && topCounterparts && (
            <Card className="p-3 bg-white">
              <h3 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                <TrendingUp size={12} />
                Top Contrapartes
              </h3>
              
              {loadingCounterparts ? (
                <div className="text-center py-3">
                  <RefreshCw size={14} className="animate-spin mx-auto text-slate-400" />
                </div>
              ) : (
                <div className="space-y-2">
                  {topCounterparts.top_entradas?.slice(0, 2).map((cp, i) => (
                    <div 
                      key={`in-${i}`} 
                      className="text-xs bg-green-50 rounded p-2 cursor-pointer hover:bg-green-100"
                      onClick={() => {
                        setAccountId(cp.cuenta);
                        loadAccountLinks(cp.cuenta);
                      }}
                    >
                      <div className="flex items-center gap-1 text-green-700 text-xs mb-1">
                        <ArrowDownLeft size={10} />
                        Origen
                      </div>
                      <div className="font-mono truncate text-slate-600">{cp.cuenta.substring(0, 20)}...</div>
                      <div className="flex justify-between mt-1">
                        <span className="text-green-700 font-medium">{formatMoney(cp.monto_total)}</span>
                        <span className="text-slate-500">{cp.cantidad_txs} txs</span>
                      </div>
                    </div>
                  ))}
                  
                  {topCounterparts.top_salidas?.slice(0, 2).map((cp, i) => (
                    <div 
                      key={`out-${i}`} 
                      className="text-xs bg-red-50 rounded p-2 cursor-pointer hover:bg-red-100"
                      onClick={() => {
                        setAccountId(cp.cuenta);
                        loadAccountLinks(cp.cuenta);
                      }}
                    >
                      <div className="flex items-center gap-1 text-red-700 text-xs mb-1">
                        <ArrowUpRight size={10} />
                        Destino
                      </div>
                      <div className="font-mono truncate text-slate-600">{cp.cuenta.substring(0, 20)}...</div>
                      <div className="flex justify-between mt-1">
                        <span className="text-red-700 font-medium">{formatMoney(cp.monto_total)}</span>
                        <span className="text-slate-500">{cp.cantidad_txs} txs</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* Graph Summary */}
          {rawData?.summary && (
            <Card className="p-3 bg-white">
              <h3 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                <DollarSign size={12} />
                Resumen del Grafo
              </h3>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-500">Vol. Entrada:</span>
                  <span className="font-medium text-green-600">{formatMoney(rawData.summary.total_volumen_entrada)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Vol. Salida:</span>
                  <span className="font-medium text-red-600">{formatMoney(rawData.summary.total_volumen_salida)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Transacciones:</span>
                  <span className="font-medium">{rawData.summary.total_transacciones?.toLocaleString()}</span>
                </div>
                <div className="border-t pt-1 mt-1">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Alto Riesgo:</span>
                    <span className={`font-medium ${rawData.summary.nodos_alto_riesgo > 0 ? 'text-red-600' : ''}`}>
                      {rawData.summary.nodos_alto_riesgo}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Con Alertas:</span>
                    <span className={`font-medium ${rawData.summary.nodos_con_alertas > 0 ? 'text-amber-600' : ''}`}>
                      {rawData.summary.nodos_con_alertas}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Sospechosos:</span>
                    <span className={`font-medium ${rawData.summary.nodos_sospechosos > 0 ? 'text-red-600' : ''}`}>
                      {rawData.summary.nodos_sospechosos}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Selected Edge Info */}
          {selectedEdge && (
            <Card className="p-3 bg-white">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                  <Activity size={12} />
                  Conexión
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setSelectedEdge(null)} className="h-6 w-6 p-0">
                  <X size={12} />
                </Button>
              </div>
              
              <div className={`flex items-center gap-2 p-2 rounded text-xs ${
                selectedEdge.direction === 'salida' ? 'bg-red-50' : 'bg-green-50'
              }`}>
                {selectedEdge.direction === 'salida' ? (
                  <ArrowUpRight className="text-red-600" size={14} />
                ) : (
                  <ArrowDownLeft className="text-green-600" size={14} />
                )}
                <span className={selectedEdge.direction === 'salida' ? 'text-red-700' : 'text-green-700'}>
                  {selectedEdge.direction === 'salida' ? 'Envío' : 'Recepción'}
                </span>
              </div>
              
              <div className="mt-2 bg-slate-50 rounded p-2">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-slate-500">Monto Total</span>
                    <p className="font-bold text-slate-900">{formatMoney(selectedEdge.monto_total)}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Transacciones</span>
                    <p className="font-bold text-slate-900">{selectedEdge.cantidad_txs}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Promedio</span>
                    <p className="font-bold text-slate-900">{formatMoney(selectedEdge.monto_promedio)}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Frecuencia</span>
                    <p className="font-bold text-slate-900">{selectedEdge.frecuencia_dias}/día</p>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default LinkAnalysisPage;
